import type { AccrualVaultV2, MarketId } from '@morpho-org/blue-sdk'
import type { InterfaceToken } from '@morpho-org/dapp-core'
import type { previewVaultV2InKindRedeem } from '@morpho-org/morpho-sdk'
import type { Address, Log } from 'viem'

import { MathLib } from '@morpho-org/blue-sdk'
import { blueAbi, vaultV2Abi } from '@morpho-org/morpho-sdk/abis'
import { getChainAddress } from '@morpho-org/morpho-sdk/addresses'
import { erc20Abi, isAddressEqual, parseEventLogs } from 'viem'

import {
  capExitToMarketCapacity,
  capInKindExitToSharesWithFee,
  isMarketV1AdapterV2
} from './deallocation'

/** One market entry of the SDK's in-kind redeem preview, shared by the IKR dialogs. */
export type InKindRedemptionPreview = ReturnType<typeof previewVaultV2InKindRedeem>[number]

/** Display assets for the review/confirm recap; id/params live on the SDK preview. */
export interface InKindRedemptionMarket {
  /** Null for idle markets (no collateral). */
  collateralAsset: InterfaceToken | null
  loanAsset: InterfaceToken
}

/** Guards chains where VaultExitBundlesV1 is not deployed yet (`UnknownAddressError`). */
export function hasInKindRedemptionBundle(chainId: number): boolean {
  try {
    return !!getChainAddress(chainId, 'bundles.vaultExitBundlesV1')
  } catch {
    return false
  }
}

/**
 * Exactly one MorphoMarketV1AdapterV2 — same gate as the SDK / preview, used to show
 * the entry CTA before an amount is picked.
 */
export function isInKindRedemptionEligibleVault(vaultV2: AccrualVaultV2): boolean {
  if (vaultV2.accrualAdapters.length !== 1) return false
  const [adapter] = vaultV2.accrualAdapters
  return adapter != null && isMarketV1AdapterV2(adapter)
}

/**
 * The sole adapter's force-deallocate penalty rate (WAD-scaled). Drives the
 * worst-case recap of Phase 1: the review prices the full exit at this rate, so
 * the realised outcome can only be at or above what was shown.
 */
export function inKindRedemptionPenaltyRate(vaultV2: AccrualVaultV2): bigint {
  const [adapter] = vaultV2.accrualAdapters
  return adapter ? (vaultV2.forceDeallocatePenalties[adapter.address] ?? 0n) : 0n
}

/**
 * The force-deallocation penalty an in-kind redemption of `requestedExitAssets` would charge.
 *
 * Same arithmetic as the SDK's `previewVaultV2InKindRedeem`, minus the market: idle assets are
 * served first and penalty-free, and only the remainder is priced at the penalty rate. Nothing
 * here depends on which market the user ends up choosing, because the penalty is a property of
 * the adapter and in-kind eligibility already requires the vault to have exactly one — so the
 * form can disclose the fee before the chooser exists. The one case this overstates is a market
 * whose capacity caps the exit below the request, and overstating a fee is the safe direction.
 */
export function inKindRedemptionFeeAssets(
  vaultV2: AccrualVaultV2,
  requestedExitAssets: bigint
): bigint {
  const penalty = inKindRedemptionPenaltyRate(vaultV2)
  const inKindAssets = MathLib.zeroFloorSub(requestedExitAssets, vaultV2.assetBalance)
  return MathLib.wMulUp(MathLib.wDivDown(inKindAssets, MathLib.WAD + penalty), penalty)
}

/** Two limits on one Market's in-kind leg, kept apart because they fail for different reasons. */
export interface InKindRedemptionCapacity {
  /** The Market's own room: its allocation, held under the capacity margin. Nothing about the
   * holder is in here, so a zero means no holder can redeem into this Market at all. */
  marketAssets: bigint
  /** `marketAssets` capped by the shares this holder actually has — what the chooser quotes,
   * since a ceiling they cannot reach is not a number they can act on. */
  reachableAssets: bigint
}

/**
 * The most a Market can take of an in-kind exit, penalty included — the chooser's `Capacity`.
 *
 * `maxExitAssets` is the whole exit the Market makes possible, and the Vault's idle balance sits
 * inside it: an exit spends idle first, penalty-free and against no allocation, so the ceiling
 * pads every Market by the same cash. So take the wallet leg back out — the split
 * `toSafeInKindPreview` applies to a selected row — and the position the holder receives is what
 * is left, net of the fee.
 *
 * `userVaultShares` is optional: while the balance is in flight only the Market's own limit
 * applies, and the two figures coincide.
 */
export function inKindRedemptionMarketCapacity(
  vaultV2: AccrualVaultV2,
  maxExitAssets: bigint,
  userVaultShares: bigint | undefined
): InKindRedemptionCapacity {
  const exitCeiling = capExitToMarketCapacity({ exitAssets: maxExitAssets, maxExitAssets })
  const reachableExit =
    userVaultShares === undefined
      ? exitCeiling
      : capInKindExitToSharesWithFee({
          exitAssets: exitCeiling,
          userVaultShares,
          vault: vaultV2,
          feeOf: assets => inKindRedemptionFeeAssets(vaultV2, assets)
        })
  return {
    marketAssets: MathLib.zeroFloorSub(exitCeiling, vaultV2.assetBalance),
    reachableAssets: MathLib.zeroFloorSub(reachableExit, vaultV2.assetBalance)
  }
}

/**
 * Restates a preview against the assets the bundle will really exit.
 *
 * The transaction sends `safeExitAssets` (clamped by `capInKindExitToShares`), not
 * `preview.exitAssets`, so every screen that quotes a preview must quote the restated one
 * or it promises more than the bundle delivers. Same arithmetic as the SDK's own
 * `previewVaultV2InKindRedeem`, with `exitAssets` swapped: idle is served first and
 * penalty-free, and only shrinks once the clamp bites into it.
 *
 * Idempotent in practice: re-applying it with a `safeExitAssets` already equal to
 * `preview.exitAssets` returns the same numbers.
 *
 * `remainingExitAssets` stays unchanged because it represents the Market's capacity shortfall,
 * which drives the chooser's capacity callouts. The clamp's residual is a deliberate share drift
 * reserve, not a capacity shortfall, so it is not reported through `remainingExitAssets`.
 * `maxExitAssets` is deliberately left alone: it is the Market's own ceiling, derived from the
 * vault's idle assets and the Market allocation, so nothing about clamping the request can move it.
 */
export function toSafeInKindPreview<
  T extends Pick<
    InKindRedemptionPreview,
    'exitAssets' | 'idleAssets' | 'netAssets' | 'feeAssets' | 'remainingExitAssets'
  >
>(preview: T, safeExitAssets: bigint, penalty: bigint): T {
  const idleAssets = MathLib.min(safeExitAssets, preview.idleAssets)
  const netAssets = MathLib.wDivDown(safeExitAssets - idleAssets, MathLib.WAD + penalty)
  return {
    ...preview,
    exitAssets: safeExitAssets,
    idleAssets,
    netAssets,
    feeAssets: MathLib.wMulUp(netAssets, penalty)
  }
}

/**
 * What the redemption actually produced, read off the confirmed receipt.
 * Named after the SDK preview (`idleAssets` / `feeAssets` / `netAssets`) so the
 * worst-case and realised recaps speak the same vocabulary.
 */
export interface InKindRedemptionReceiptAmounts {
  /** Vault assets the bundler paid straight to the wallet — penalty-free. */
  idleAssets: bigint
  /** Force-deallocation penalty actually charged, not the vault's maximum. */
  feeAssets: bigint
  /** Assets supplied to the Morpho Blue market on the user's behalf. */
  netAssets: bigint
  /** Total that left the vault deposit — the three legs above, within a wei or two of the request. */
  exitAssets: bigint
}

export interface InKindRedemptionReceiptContext {
  vaultAddress: Address
  /** The Market the user picked — a replacement redeeming into another Market must not
   * report its position under this one's name. */
  marketId: MarketId
  /** The vault's underlying asset — pins the ERC20 leg away from the share token. */
  assetAddress: Address
  /** The signer the redemption was submitted for. */
  userAddress: Address
  morphoAddress: Address
  /** VaultExitBundlesV1 — the only sender that pays the idle leg to the wallet. */
  bundlerAddress: Address
}

const sumAssets = (events: { args: { assets: bigint } }[]) =>
  events.reduce((total, { args }) => total + args.assets, 0n)

/** Only the logs a given contract emitted — the topic0s below are not unique on their own. */
const logsFrom = (logs: readonly Log[], emitter: Address) =>
  logs.filter(log => isAddressEqual(log.address, emitter))

/**
 * Decodes the realised amounts of an in-kind redemption from its receipt.
 *
 * VaultExitBundlesV1 emits nothing itself, so every quantity is read directly
 * off the vault, Morpho Blue and the asset token — no shares/assets conversion
 * and no arithmetic inference. Every lookup pins the emitter *and* the user:
 * the asset and the vault share token share the ERC20 `Transfer` topic, the
 * vault emits `Withdraw` once per leg, and in the Safe / `sendCalls` path the
 * approvals land in this same receipt.
 */
export function decodeInKindRedemptionReceipt(
  logs: readonly Log[],
  {
    vaultAddress,
    marketId,
    assetAddress,
    userAddress,
    morphoAddress,
    bundlerAddress
  }: InKindRedemptionReceiptContext
): InKindRedemptionReceiptAmounts {
  const vaultLogs = logsFrom(logs, vaultAddress)

  const idleTransfers = parseEventLogs({
    abi: erc20Abi,
    logs: logsFrom(logs, assetAddress),
    eventName: 'Transfer',
    args: { from: bundlerAddress, to: userAddress }
  })
  const forceDeallocations = parseEventLogs({
    abi: vaultV2Abi,
    logs: vaultLogs,
    eventName: 'ForceDeallocate',
    args: { onBehalf: userAddress }
  })
  const supplies = parseEventLogs({
    abi: blueAbi,
    logs: logsFrom(logs, morphoAddress),
    eventName: 'Supply',
    // Pinned to the selected Market as well as the user: a same-nonce replacement that is
    // itself a valid redemption into a *different* Market otherwise satisfies the identity
    // check and gets reported under this Market's name.
    args: { id: marketId, onBehalf: userAddress }
  })
  const withdrawals = parseEventLogs({
    abi: vaultV2Abi,
    logs: vaultLogs,
    eventName: 'Withdraw',
    args: { onBehalf: userAddress }
  })

  return {
    idleAssets: idleTransfers.reduce((total, { args }) => total + args.value, 0n),
    feeAssets: forceDeallocations.reduce((total, { args }) => total + args.penaltyAssets, 0n),
    netAssets: sumAssets(supplies),
    exitAssets: sumAssets(withdrawals)
  }
}

/**
 * The receipt's amounts if — and only if — it is this redemption. The confirm dialog reaches
 * its success state through replacement detection too (a wallet cancellation, an unrelated
 * same-nonce transaction), and only the logs separate those from the real thing: a redemption's
 * three legs account for its whole withdrawal, and nothing else satisfies that identity.
 */
export function realisedInKindRedemption(
  logs: readonly Log[],
  context: InKindRedemptionReceiptContext
): InKindRedemptionReceiptAmounts | undefined {
  const amounts = decodeInKindRedemptionReceipt(logs, context)
  const isRedemption =
    amounts.exitAssets > 0n &&
    amounts.idleAssets + amounts.feeAssets + amounts.netAssets === amounts.exitAssets
  return isRedemption ? amounts : undefined
}
